import {
  type INamedDocument,
  isEnumValue,
  isINamedDocument,
  isObject,
  type Timestamp,
  TypeGuard,
} from '@principle-theorem/shared';
import { isNumber } from 'lodash';
import { IStaffer } from '../../staffer/staffer';
import { IChartedItemConfiguration } from '../charted-item-configuration';
import { ChartableSurface } from './chartable-surface';
import { type IChartedSurface } from './charted-surface';
import { type IClinicalNote } from './clinical-note';
import { Arch, Quadrant } from './quadrant';
import { type IScopeRef } from './scoped-surface';
import { type IToothRef } from './tooth';

export enum ChartedItemType {
  Generic = 'generic',
  ChartedCondition = 'chartedCondition',
  ChartedTreatment = 'chartedTreatment',
  ChartedTreatmentGroup = 'chartedTreatmentGroup',
  TreatmentStep = 'treatmentStep',
  Treatment = 'treatment',
  ChartedMultiStepTreatment = 'chartedMultiStepTreatment',
}

export interface IChartedItem<
  T extends IChartedItemConfiguration = IChartedItemConfiguration,
> {
  uuid: string;
  type: ChartedItemType;
  config: INamedDocument<T>;
  chartedSurfaces: IChartedSurface[];
  resolvedBy?: INamedDocument<IStaffer>;
  resolvedAt?: Timestamp;
  notes: IClinicalNote[];
  scopeRef: IScopeRef;
}

export interface IChartedRef {
  wholeMouth: boolean;
  arch: Arch;
  quadrant: Quadrant;
  multipleTeeth: IToothRef[];
  tooth: IChartedTooth;
  unscoped: boolean;
}

export interface IChartedTooth extends IToothRef {
  surface?: ChartableSurface;
}

export const isChartedTooth = TypeGuard.interface<IChartedTooth>({
  quadrant: TypeGuard.enumValue(Quadrant),
  quadrantIndex: isNumber,
  surface: TypeGuard.undefinedOr(TypeGuard.enumValue(ChartableSurface)),
});

export function isKnownChartableSurface(
  surface?: ChartableSurface | IChartedTooth | Partial<IChartedRef>
): surface is Exclude<ChartableSurface, ChartableSurface.Unknown> {
  if (!surface) {
    return true;
  }

  if (isEnumValue(ChartableSurface, surface)) {
    return surface !== ChartableSurface.Unknown;
  }

  if (isChartedTooth(surface)) {
    return surface.surface !== ChartableSurface.Unknown;
  }

  return surface.tooth?.surface !== ChartableSurface.Unknown;
}

export function isChartedToothWithSurface(
  data: Partial<IChartedRef>
): data is Required<Pick<IChartedRef, 'tooth'>> {
  return (
    !!data.tooth &&
    isEnumValue(ChartableSurface, data.tooth.surface) &&
    isKnownChartableSurface(data.tooth.surface)
  );
}

export function hasArch(
  data: Partial<IChartedRef>
): data is Pick<IChartedRef, 'arch'> {
  return isObject(data) && 'arch' in data && isEnumValue(Arch, data.arch);
}

export function hasQuadrant(
  data: Partial<IChartedRef>
): data is Pick<IChartedRef, 'quadrant'> {
  return (
    isObject(data) && 'quadrant' in data && isEnumValue(Quadrant, data.quadrant)
  );
}

export function hasTooth(
  data: Partial<IChartedRef>
): data is Pick<IChartedRef, 'tooth'> {
  return isObject(data) && !!data.tooth;
}

export function isChartedItem(item: unknown): item is IChartedItem {
  return (
    isObject(item) &&
    'config' in item &&
    isINamedDocument(item.config) &&
    'chartedSurfaces' in item &&
    Array.isArray(item.chartedSurfaces) &&
    'notes' in item &&
    Array.isArray(item.notes)
  );
}
